import logger from '../../utils/logger';
// Admin Context - Shared state and functions for admin components
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminContext = createContext(null);

export function AdminProvider({ children, adminKey }) {
  const { t, language } = useLanguage();
  
  // Shared State
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [kbSections, setKbSections] = useState([]);
  const [kbArticles, setKbArticles] = useState([]);
  const [adminNotes, setAdminNotes] = useState([]);
  const [jobPostings, setJobPostings] = useState([]);
  const [contributionConfig, setContributionConfig] = useState({
    enabled: true,
    default_type: 'percent',
    default_percent: 2.0,
    default_amount: 1.0,
    custom_amount_default: false,
    title: { fr: 'Contribution volontaire à PayHub Québec+', en: 'Voluntary contribution to PayHub Quebec+' },
    description: { fr: 'Merci de soutenir votre plateforme', en: 'Thank you for supporting your platform' }
  });
  const [siteContactEmail, setSiteContactEmail] = useState('internetpme@gmail.com');

  // API instance (memoized so callbacks depending on it stay stable across renders)
  const api = useMemo(() => axios.create({
    baseURL: `${API_URL}/api`,
    headers: { 'X-Admin-Key': adminKey },
    withCredentials: true
  }), [adminKey]);

  // Fetch Functions
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data);
      return response.data;
    } catch (error) {
      logger.error('Error fetching stats:', error);
    }
  }, [api]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data.users || []);
      return response.data.users || [];
    } catch (error) {
      logger.error('Error fetching users:', error);
    }
  }, [api]);

  const fetchTransactions = useCallback(async () => {
    try {
      const response = await api.get('/admin/transactions');
      setTransactions(response.data.transactions || []);
      return response.data.transactions || [];
    } catch (error) {
      logger.error('Error fetching transactions:', error);
    }
  }, [api]);

  const fetchAgencies = useCallback(async () => {
    try {
      const response = await api.get('/agencies');
      setAgencies(response.data.agencies || []);
      return response.data.agencies || [];
    } catch (error) {
      logger.error('Error fetching agencies:', error);
    }
  }, [api]);

  const fetchInvitations = useCallback(async () => {
    try {
      const response = await api.get('/agencies/invitations');
      setInvitations(response.data.invitations || []);
      return response.data.invitations || [];
    } catch (error) {
      logger.error('Error fetching invitations:', error);
    }
  }, [api]);

  const fetchApiKeys = useCallback(async () => {
    try {
      const response = await api.get('/admin/woocommerce/api-keys');
      setApiKeys(response.data.api_keys || []);
      return response.data.api_keys || [];
    } catch (error) {
      logger.error('Error fetching API keys:', error);
    }
  }, [api]);

  const fetchChecklist = useCallback(async () => {
    try {
      const response = await api.get('/admin/checklist');
      setChecklist(response.data.items || []);
      return response.data.items || [];
    } catch (error) {
      logger.error('Error fetching checklist:', error);
    }
  }, [api]);

  const fetchKB = useCallback(async () => {
    try {
      const [sectionsRes, articlesRes] = await Promise.all([
        api.get('/admin/kb/sections'),
        api.get('/admin/kb/articles')
      ]);
      setKbSections(sectionsRes.data.sections || []);
      setKbArticles(articlesRes.data.articles || []);
    } catch (error) {
      logger.error('Error fetching KB:', error);
    }
  }, [api]);

  const fetchNotes = useCallback(async () => {
    try {
      const response = await api.get('/admin/notes');
      setAdminNotes(response.data.notes || []);
      return response.data.notes || [];
    } catch (error) {
      logger.error('Error fetching notes:', error);
    }
  }, [api]);

  const fetchJobPostings = useCallback(async () => {
    try {
      const response = await api.get('/admin/job-postings');
      setJobPostings(response.data?.postings || response.data || []);
      return response.data?.postings || response.data || [];
    } catch (error) {
      logger.error('Error fetching job postings:', error);
    }
  }, [api]);

  const fetchContributionConfig = useCallback(async () => {
    try {
      const response = await api.get('/admin/contribution-config');
      if (response.data) {
        setContributionConfig(response.data);
      }
    } catch (error) {
      logger.error('Error fetching contribution config:', error);
    }
  }, [api]);

  const fetchSiteContactEmail = useCallback(async () => {
    try {
      const response = await api.get('/admin/site-config');
      if (response.data?.contact_email) {
        setSiteContactEmail(response.data.contact_email);
      }
    } catch (error) {
      logger.error('Error fetching site config:', error);
    }
  }, [api]);

  const fetchAllData = useCallback(async () => {
    await Promise.all([
      fetchStats(),
      fetchUsers(),
      fetchTransactions(),
      fetchKB(),
      fetchNotes(),
      fetchApiKeys(),
      fetchJobPostings(),
      fetchSiteContactEmail(),
      fetchChecklist(),
      fetchContributionConfig(),
      fetchAgencies(),
      fetchInvitations()
    ]);
  }, [fetchStats, fetchUsers, fetchTransactions, fetchKB, fetchNotes, fetchApiKeys, fetchJobPostings, fetchSiteContactEmail, fetchChecklist, fetchContributionConfig, fetchAgencies, fetchInvitations]);

  // Utility Functions
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(language === 'fr' ? 'Copié!' : 'Copied!');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const value = {
    // State
    stats, setStats,
    users, setUsers,
    transactions, setTransactions,
    agencies, setAgencies,
    invitations, setInvitations,
    apiKeys, setApiKeys,
    checklist, setChecklist,
    kbSections, setKbSections,
    kbArticles, setKbArticles,
    adminNotes, setAdminNotes,
    jobPostings, setJobPostings,
    contributionConfig, setContributionConfig,
    siteContactEmail, setSiteContactEmail,
    // API
    api,
    adminKey,
    // Fetch Functions
    fetchStats,
    fetchUsers,
    fetchTransactions,
    fetchAgencies,
    fetchInvitations,
    fetchApiKeys,
    fetchChecklist,
    fetchKB,
    fetchNotes,
    fetchJobPostings,
    fetchContributionConfig,
    fetchSiteContactEmail,
    fetchAllData,
    // Utilities
    copyToClipboard,
    formatDate,
    // Language
    t,
    language
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
}

export default AdminContext;
